// M004-F0397 / T-M004-0018 — non-blocking, docked corner upload manager.
// Design: docs/design/M004-D015-User-Video-Library.md §8.2/§8.3 (studioaeye-backend).
//
// This module owns ONLY the queue/concurrency/presentation layer. It never
// reimplements chunking, finalize-poll-fallback, or retry semantics: every
// file's actual transfer is delegated to a caller-supplied `beginUpload(file)`
// function, which the choreography Video Library wires to the existing
// `videoIngestApi.finalizeThenAwaitReady` pipeline (createVideoIngest ->
// uploadChunks -> finalizeThenAwaitReady -> completeVideoIngest), so the
// resumable-ingest contract proven by videoIngestApi.test.js is reused
// unchanged.
//
// `beginUpload(file, { onProgress, signal })` must:
//   - call onProgress({ phase, percent }) as the transfer advances (phase is
//     a short display string, percent is 0..100);
//   - resolve with the completed media row on success;
//   - reject with an Error (any shape videoIngestApi already throws) on
//     failure — the manager surfaces error.message and offers retry, which
//     simply calls beginUpload again for that same file (a real resumable
//     RETRY, not a restart, when the underlying uploadId/session survives;
//     videoIngestApi's own restart-required codes already produce a fresh
//     claim via the caller's beginUpload closure).
//
// Concurrency: MAX_CONCURRENT files upload at once; the rest queue and start
// as slots free. The manager never blocks the rest of the page and never
// steals focus — it is a fixed, non-modal, dismissible-when-idle corner
// panel, collapsible to a compact summary, and persists for as long as the
// mounting parent (the Video Library tab) stays mounted while uploads run.

import React, { useCallback, useEffect, useRef, useState } from 'react';

const MAX_CONCURRENT = 3;
let nextRowId = 1;

const TERMINAL_STATES = new Set(['done', 'failed']);

export function useUploadQueueManager(beginUpload) {
  const [rows, setRows] = useState([]); // [{ id, file, name, state, percent, phase, error, media }]
  const [collapsed, setCollapsed] = useState(false);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const inFlightRef = useRef(new Set());

  const patchRow = useCallback((id, patch) => {
    setRows((current) => current.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  }, []);

  const runUpload = useCallback((id, file) => {
    inFlightRef.current.add(id);
    patchRow(id, { state: 'uploading', error: '' });
    Promise.resolve(beginUpload(file, {
      onProgress: ({ phase, percent } = {}) => {
        patchRow(id, {
          state: phase === 'processing' || phase === 'validating' ? 'processing' : 'uploading',
          phase: phase || 'uploading',
          percent: typeof percent === 'number' ? percent : undefined,
        });
      },
    })).then((media) => {
      inFlightRef.current.delete(id);
      patchRow(id, { state: 'done', percent: 100, media });
    }).catch((error) => {
      inFlightRef.current.delete(id);
      patchRow(id, { state: 'failed', error: error?.message || 'This upload failed.' });
    });
  }, [beginUpload, patchRow]);

  // Advance the queue: start queued rows while concurrency slots are free.
  useEffect(() => {
    const uploading = rows.filter((row) => row.state === 'uploading' || row.state === 'processing').length;
    let free = MAX_CONCURRENT - uploading;
    if (free <= 0) return;
    for (const row of rows) {
      if (free <= 0) break;
      if (row.state === 'queued' && !inFlightRef.current.has(row.id)) {
        free -= 1;
        runUpload(row.id, row.file);
      }
    }
  }, [rows, runUpload]);

  const enqueueFiles = useCallback((files) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;
    setCollapsed(false);
    setRows((current) => [
      ...current,
      ...list.map((file) => ({
        id: `upload-${nextRowId++}`, file, name: file.name, state: 'queued', percent: 0, phase: 'Queued', error: '', media: null,
      })),
    ]);
  }, []);

  const retry = useCallback((id) => {
    const row = rowsRef.current.find((entry) => entry.id === id);
    if (!row) return;
    patchRow(id, { state: 'queued', percent: 0, phase: 'Queued', error: '' });
  }, [patchRow]);

  const dismiss = useCallback((id) => {
    setRows((current) => current.filter((row) => row.id !== id));
  }, []);

  const idle = rows.every((row) => TERMINAL_STATES.has(row.state));

  const dismissAll = useCallback(() => {
    if (!idle) return;
    setRows([]);
  }, [idle]);

  return {
    rows, collapsed, setCollapsed, enqueueFiles, retry, dismiss, dismissAll, idle,
    completedMedia: rows.filter((row) => row.state === 'done' && row.media).map((row) => row.media),
    acknowledgeCompleted: (id) => dismiss(id),
  };
}

const STATE_LABEL = { queued: 'Queued', uploading: 'Uploading', processing: 'Processing', done: 'Done', failed: 'Failed' };

function UploadRow({ row, onRetry, onDismiss }) {
  return (
    <li className={`mc-upload-row mc-upload-row-${row.state}`}>
      <div className="mc-upload-row-head">
        <span className="mc-upload-row-name" title={row.name}>{row.name}</span>
        <span className="mc-upload-row-state">{STATE_LABEL[row.state] || row.state}</span>
      </div>
      <div className="mc-progress-bar" aria-hidden="true"><div className="mc-progress-fill" style={{ width: `${row.state === 'done' ? 100 : row.percent || 0}%` }} /></div>
      {row.state === 'failed' && (
        <div className="mc-upload-row-actions">
          <span className="mc-upload-row-error">{row.error}</span>
          <button type="button" className="mc-quiet" onClick={() => onRetry(row.id)}>Retry</button>
        </div>
      )}
      {row.state === 'done' && (
        <div className="mc-upload-row-actions">
          <button type="button" className="mc-quiet" aria-label={`Dismiss ${row.name}`} onClick={() => onDismiss(row.id)}>Dismiss</button>
        </div>
      )}
    </li>
  );
}

// Docked, non-modal, collapsible corner panel. Keyboard-reachable (normal tab
// order — nothing here is a modal/focus trap); progress announced through a
// single polite aria-live region so screen readers hear state changes without
// interrupting the user's current focus.
export default function UploadQueueManager({ manager }) {
  const { rows, collapsed, setCollapsed, retry, dismiss, dismissAll, idle } = manager;
  if (rows.length === 0) return null;

  const activeCount = rows.filter((row) => row.state === 'uploading' || row.state === 'processing' || row.state === 'queued').length;
  const summary = activeCount > 0 ? `Uploading ${activeCount}…` : 'Uploads complete';
  const liveAnnouncement = rows.map((row) => `${row.name}: ${STATE_LABEL[row.state] || row.state}${row.state === 'uploading' ? ` ${row.percent || 0}%` : ''}`).join('. ');

  return (
    <div className={`mc-upload-manager ${collapsed ? 'is-collapsed' : ''}`} role="region" aria-label="Upload manager">
      <div className="mc-upload-manager-head">
        <button
          type="button"
          className="mc-upload-manager-toggle"
          aria-expanded={!collapsed}
          onClick={() => setCollapsed((value) => !value)}
        >
          <span>{summary}</span>
          <span aria-hidden="true">{collapsed ? '▲' : '▼'}</span>
        </button>
        {idle && (
          <button type="button" className="mc-quiet mc-upload-manager-dismiss" aria-label="Dismiss upload manager" onClick={dismissAll}>✕</button>
        )}
      </div>
      <div className="mc-visually-hidden" role="status" aria-live="polite">{liveAnnouncement}</div>
      {!collapsed && (
        <ul className="mc-upload-manager-rows">
          {rows.map((row) => <UploadRow key={row.id} row={row} onRetry={retry} onDismiss={dismiss} />)}
        </ul>
      )}
    </div>
  );
}
